(() => {
  const initAmbassador = (root) => {
    if (!root || root.dataset.oluAmbassadorReady === 'true') return;
    root.dataset.oluAmbassadorReady = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealItems = root.querySelectorAll('[data-amb-reveal]');

    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
    } else {
      const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observerInstance.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -38px 0px' });

      revealItems.forEach((item) => observer.observe(item));
    }

    const hero = root.querySelector('.amb-next__hero');
    const finePointer = window.matchMedia('(pointer: fine)').matches;

    if (hero && finePointer && !reduceMotion) {
      hero.addEventListener('pointermove', (event) => {
        const bounds = hero.getBoundingClientRect();
        const x = (event.clientX - bounds.left) / bounds.width - 0.5;
        const y = (event.clientY - bounds.top) / bounds.height - 0.5;
        root.style.setProperty('--amb-px', x.toFixed(3));
        root.style.setProperty('--amb-py', y.toFixed(3));
      });
      hero.addEventListener('pointerleave', () => {
        root.style.setProperty('--amb-px', '0');
        root.style.setProperty('--amb-py', '0');
      });
    }

    const floatSticker = root.querySelector('[data-amb-float]');
    if (floatSticker && finePointer && !reduceMotion) {
      floatSticker.addEventListener('pointermove', (event) => {
        const rect = floatSticker.getBoundingClientRect();
        const rotate = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
        const lift = ((event.clientY - rect.top) / rect.height - 0.5) * -8;
        floatSticker.style.transform = `translateY(${lift}px) rotate(${rotate}deg) scale(1.04)`;
      });
      floatSticker.addEventListener('pointerleave', () => {
        floatSticker.style.transform = '';
      });
    }

    const looksRoot = root.querySelector('[data-amb-looks]');
    if (looksRoot) {
      const viewport = looksRoot.querySelector('[data-amb-looks-viewport]');
      const track = looksRoot.querySelector('.amb-next__looks-track');
      const cards = Array.from(looksRoot.querySelectorAll('[data-amb-look]'));
      const previous = looksRoot.querySelector('[data-amb-look-prev]');
      const next = looksRoot.querySelector('[data-amb-look-next]');
      const count = looksRoot.querySelector('[data-amb-look-count]');
      let activeIndex = 0;
      let dragStartX = 0;
      let dragStartScroll = 0;
      let isDragging = false;
      let scrollFrame = 0;

      const updateLookUI = (index) => {
        if (!cards.length) return;
        activeIndex = Math.max(0, Math.min(cards.length - 1, index));
        cards.forEach((card, cardIndex) => {
          const active = cardIndex === activeIndex;
          card.classList.toggle('is-active', active);
          card.setAttribute('aria-current', active ? 'true' : 'false');
        });
        if (count) count.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
        if (previous) previous.disabled = activeIndex === 0;
        if (next) next.disabled = activeIndex === cards.length - 1;
      };

      const closestCardIndex = () => {
        if (!track || !cards.length) return 0;
        const trackCenter = track.scrollLeft + track.clientWidth / 2;
        let closestIndex = 0;
        let smallestDistance = Number.POSITIVE_INFINITY;
        cards.forEach((card, index) => {
          const center = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(center - trackCenter);
          if (distance < smallestDistance) {
            smallestDistance = distance;
            closestIndex = index;
          }
        });
        return closestIndex;
      };

      const goToLook = (index) => {
        if (!track || !cards.length) return;
        const safeIndex = Math.max(0, Math.min(cards.length - 1, index));
        const card = cards[safeIndex];
        track.scrollTo({
          left: card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
        updateLookUI(safeIndex);
      };

      if (cards.length) {
        updateLookUI(0);
        previous?.addEventListener('click', () => goToLook(activeIndex - 1));
        next?.addEventListener('click', () => goToLook(activeIndex + 1));

        track?.addEventListener('scroll', () => {
          if (scrollFrame) return;
          scrollFrame = requestAnimationFrame(() => {
            updateLookUI(closestCardIndex());
            scrollFrame = 0;
          });
        }, { passive: true });

        cards.forEach((card, index) => {
          card.addEventListener('click', () => goToLook(index));
        });

        viewport?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToLook(activeIndex - 1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToLook(activeIndex + 1);
          }
        });

        if (track && finePointer && !reduceMotion) {
          track.addEventListener('pointerdown', (event) => {
            isDragging = true;
            dragStartX = event.clientX;
            dragStartScroll = track.scrollLeft;
            track.classList.add('is-dragging');
            track.setPointerCapture?.(event.pointerId);
          });
          track.addEventListener('pointermove', (event) => {
            if (!isDragging) return;
            track.scrollLeft = dragStartScroll - (event.clientX - dragStartX);
          });
          const stopDrag = (event) => {
            if (!isDragging) return;
            isDragging = false;
            track.classList.remove('is-dragging');
            track.releasePointerCapture?.(event.pointerId);
            goToLook(closestCardIndex());
          };
          track.addEventListener('pointerup', stopDrag);
          track.addEventListener('pointercancel', stopDrag);
          track.addEventListener('pointerleave', (event) => {
            if (isDragging && event.buttons === 0) stopDrag(event);
          });
        }
      }
    }


    let frame = 0;
    const updateProgress = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        const top = root.getBoundingClientRect().top + window.scrollY;
        const distance = root.offsetHeight - window.innerHeight;
        const progress = distance > 0 ? ((window.scrollY - top) / distance) * 100 : 0;
        root.style.setProperty('--amb-scroll', `${Math.max(0, Math.min(100, progress))}%`);
        frame = 0;
      });
    };

    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-olu-ambassador]').forEach(initAmbassador);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
